/**
 * SCM6 — auto-derive supply-chain risks for the report's `risks` section.
 *
 * Pure function. Called from the typed report generation pipeline
 * (`src/services/siteRagService.ts`) at gen time. Output is merged
 * into `SiteRagReport.risks` so the rendered Risk Assessment section
 * gains a "Supply Chain Risks" sub-block.
 *
 * Risk surface (per spec):
 *   - Sole-source critical-path items
 *   - Transformer / switchgear shortage (high lead-time power-conversion items)
 *   - Items with negative slack vs the project COD target
 *
 * TODO(orchestrator): SCM-Data will land the canonical
 * `src/types/supplyChain.ts`. The local stub in this branch matches
 * the spec shapes so a swap is mechanical at merge time.
 *
 * TODO(orchestrator): SCM5 (Agent B) will land
 * `src/lib/criticalPathAnalysis.ts`. The `computeCriticalPath`
 * helper below is a SCM6-local stand-in so risk derivation can
 * compute negative-slack flags without depending on Agent B's
 * surface. At merge time the orchestrator should replace the local
 * helper with an import from SCM5's module.
 */
import type {
  BomItem,
  Supplier,
  SupplierRiskAssessment,
  SupplyChainPlan,
} from '../types/supplyChain';
import { normaliseCertification } from '../types/supplyChain';
import type { ProjectIntent, RiskItem } from '../types/siteRag';
import {
  geopoliticalRiskFor,
  type GeopoliticalRiskLevel,
} from './geopoliticalRisk';
import { effectiveTimeline } from './projectIntent';

/**
 * One element on the computed critical-path. Mirrors the field shape
 * SCM5 is expected to expose (`item`, `latestOrderDate`,
 * `slackMonths`) so the merge-time swap is trivial.
 */
interface CriticalPathEntry {
  item: BomItem;
  /** ISO date — last day to order without slipping COD. */
  latestOrderDate: string;
  /** Months between today and `latestOrderDate`. Negative = already late. */
  slackMonths: number;
}

/** Project timeline bucket → assumed months from "today" to COD. */
const TIMELINE_TO_MONTHS: Record<NonNullable<ProjectIntent['timeline']>, number> = {
  'under-2yr': 18,
  '2-5yr': 36,
  'over-5yr': 72,
  'undecided': 36,
};

const SHORTAGE_LEAD_MONTHS = 24;
const SOLE_SOURCE_SEVERITY: RiskItem['severity'] = 'high';
const SHORTAGE_SEVERITY: RiskItem['severity'] = 'medium';
const OVERDUE_SEVERITY: RiskItem['severity'] = 'high';

/**
 * Default install duration in months (post-arrival on-site).
 * Tuned so the critical-path math is plausible without a fully-
 * specified BoM; SCM5 will provide a richer per-category model.
 */
const DEFAULT_INSTALL_MONTHS = 3;

export interface DeriveSupplyChainRisksOptions {
  /** Override "now" for deterministic test output. ISO string. */
  now?: string;
  /**
   * Override the project COD horizon (months from `now`). When set,
   * takes precedence over `intent.timeline`. Lets the report
   * pipeline pass the exact in-service date when the user has
   * provided one in `FinancialAssumptions`.
   */
  monthsToCod?: number;
}

/**
 * Derive supply-chain risks from a SupplyChainPlan.
 *
 * Returns an empty array when `plan.bom` is empty — caller is
 * responsible for short-circuiting "no plan" early.
 */
export function deriveSupplyChainRisks(
  plan: SupplyChainPlan,
  intent: ProjectIntent | null | undefined,
  options: DeriveSupplyChainRisksOptions = {},
): RiskItem[] {
  if (!plan || !Array.isArray(plan.bom) || plan.bom.length === 0) return [];
  const risks: RiskItem[] = [];

  // ── Sole-source critical-path items ──────────────────────────────
  const soleSource = plan.bom.filter(
    (item) =>
      item.criticality === 'critical-path' &&
      item.substitutability === 'sole-source',
  );
  if (soleSource.length > 0) {
    risks.push({
      id: `scm-sole-source-${soleSource.length}`,
      category: 'execution',
      label: `${soleSource.length} sole-source critical-path item${soleSource.length === 1 ? '' : 's'}`,
      severity: SOLE_SOURCE_SEVERITY,
      impact: `Items with no qualified substitute: ${soleSource
        .map((i) => i.name)
        .join(', ')}. Loss of supplier or delivery failure delays project COD.`,
      mitigation:
        'Maintain pre-qualified secondary supplier OR negotiate take-or-pay slot reservations.',
    });
  }

  // ── Transformer / switchgear shortage flag ───────────────────────
  // WHY: 2024-2026 market shortage in large power transformers and
  //      HV switchgear extends lead times past 24 months. Universal
  //      surfacing keeps the report honest even when the BoM author
  //      didn't flag the item as critical-path.
  const shortageHits = plan.bom.filter(
    (item) =>
      item.category === 'power-conversion' &&
      item.leadTimeMonthsHigh >= SHORTAGE_LEAD_MONTHS,
  );
  if (shortageHits.length > 0) {
    risks.push({
      id: 'scm-transformer-shortage',
      category: 'infrastructure',
      label: 'Power transformer / switchgear shortage',
      severity: SHORTAGE_SEVERITY,
      impact:
        'Global power transformer market shortage 2024-2026; lead times up to 36 months. Constrains interconnection timeline.',
      mitigation:
        'Initiate RFQ no later than 36 months before COD; consider used / refurbished as bridge.',
    });
  }

  // ── Negative-slack items (already late vs COD) ───────────────────
  const overdue = computeCriticalPath(plan, intent, options).filter(
    (entry) => entry.slackMonths < 0,
  );
  if (overdue.length > 0) {
    risks.push({
      id: `scm-overdue-${overdue.length}`,
      category: 'execution',
      label: `${overdue.length} item${overdue.length === 1 ? '' : 's'} already late vs COD target`,
      severity: OVERDUE_SEVERITY,
      impact: `Latest-order-date for these items has passed: ${overdue
        .map((e) => e.item.name)
        .join(
          ', ',
        )}. Project COD will slip without immediate procurement action OR substitution to shorter-lead alternatives.`,
      mitigation:
        'Accelerate procurement OR adjust COD OR substitute equivalent products.',
    });
  }

  return risks;
}

/**
 * Local critical-path helper. SCM5 (Agent B) will land a richer
 * version at `src/lib/criticalPathAnalysis.ts`; at merge time
 * replace the call site above with an import from that module.
 *
 * Per-item slack model (deliberately simple):
 *
 *   slackMonths = monthsToCod - leadTimeMonthsHigh - DEFAULT_INSTALL_MONTHS
 *
 * Treats `leadTimeMonthsHigh` as the conservative lead time. Negative
 * slack means the latest-order-date has passed — the item is already
 * late vs the project COD target.
 */
function computeCriticalPath(
  plan: SupplyChainPlan,
  intent: ProjectIntent | null | undefined,
  options: DeriveSupplyChainRisksOptions,
): CriticalPathEntry[] {
  const monthsToCod = resolveMonthsToCod(intent, options);
  const nowMs = options.now ? Date.parse(options.now) : Date.now();
  return plan.bom.map((item) => {
    const slackMonths =
      monthsToCod - item.leadTimeMonthsHigh - DEFAULT_INSTALL_MONTHS;
    const latestOrderMs =
      nowMs + slackMonths * 30 * 24 * 60 * 60 * 1000;
    return {
      item,
      latestOrderDate: new Date(latestOrderMs).toISOString(),
      slackMonths,
    };
  });
}

function resolveMonthsToCod(
  intent: ProjectIntent | null | undefined,
  options: DeriveSupplyChainRisksOptions,
): number {
  // Explicit override wins — used by the report pipeline when a precise
  // COD lives elsewhere (e.g. `FinancialAssumptions.inServiceDate`).
  if (typeof options.monthsToCod === 'number') return options.monthsToCod;
  // PMT4 headline path: when the user entered an exact COD on the
  // intent, use the precise month horizon instead of the bucket
  // midpoint. This is the integration that turns "5+ years out → 72
  // months → transformer (36 mo lead) has 36 mo slack" into
  // "23 months until COD → transformer has -13 mo slack → CRITICAL".
  const nowRef = options.now ? new Date(options.now) : new Date();
  const eff = effectiveTimeline(intent, nowRef);
  if (eff.isExact && typeof eff.monthsAway === 'number' && Number.isFinite(eff.monthsAway)) {
    return eff.monthsAway;
  }
  return TIMELINE_TO_MONTHS[eff.bucket] ?? TIMELINE_TO_MONTHS['undecided'];
}

// ─────────────────────────────────────────────────────────────────────────────
// SCM14 — supplier-level risk assessment
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Horizon (days) for the "certs expiring soon" flag. 90 days is a
 * deliberately conservative window — long enough that procurement has
 * time to chase renewals before an order is blocked, short enough that
 * the flag doesn't fire on most well-maintained suppliers.
 */
export const CERT_EXPIRY_WINDOW_DAYS = 90;

const TIER_RANK: Record<GeopoliticalRiskLevel, number> = {
  low: 0,
  medium: 1,
  high: 2,
  critical: 3,
};

const RANK_TIER: readonly GeopoliticalRiskLevel[] = [
  'low',
  'medium',
  'high',
  'critical',
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Derive the SCM14 risk assessment for a single supplier.
 *
 * Pure; deterministic given (supplier, now). The three component
 * tiers are computed independently and the overall tier is the
 * worst-of-three:
 *
 *   - Geopolitical: from `geopoliticalRiskFor(supplier.hq.country)`.
 *   - Cert expiry:  any cert with `expiresAt` ≤ now + 90 days bumps
 *                   the contribution to 'medium'. Already-expired
 *                   certs (expiresAt < now) bump it to 'high'.
 *   - Financial:    'investment-grade' → low, 'speculative' → medium,
 *                   'distressed' → critical, 'unknown' → medium
 *                   (conservative middle, matching the geopolitical
 *                    default-on-unknown convention).
 *
 * The 'unknown' financial mapping is deliberately not 'low' — we don't
 * want a missing rating to hide a real risk from the UI pill. Once D&B
 * is wired in Phase 3 the 'unknown' tier should be rare.
 */
export function supplierRiskAssessment(
  supplier: Supplier,
  now: Date | string = new Date(),
): SupplierRiskAssessment {
  const nowMs = typeof now === 'string' ? Date.parse(now) : now.getTime();
  if (!Number.isFinite(nowMs)) {
    // Fall back to current time — invalid input shouldn't crash the
    // assessment helper; instead it degrades gracefully so the UI
    // still renders.
    return supplierRiskAssessment(supplier, new Date());
  }

  // ── Geopolitical ─────────────────────────────────────────────────
  const geo = geopoliticalRiskFor(supplier.hq?.country);

  // ── Certifications: window + already-expired counts ───────────────
  const horizonMs = nowMs + CERT_EXPIRY_WINDOW_DAYS * MS_PER_DAY;
  let expiringSoon = 0;
  let expired = 0;
  for (const raw of supplier.certifications ?? []) {
    const cert = normaliseCertification(raw);
    if (!cert.expiresAt) continue;
    const expMs = Date.parse(cert.expiresAt);
    if (!Number.isFinite(expMs)) continue;
    if (expMs < nowMs) {
      expired += 1;
      expiringSoon += 1; // already-expired is a strict subset of "≤ horizon"
    } else if (expMs <= horizonMs) {
      expiringSoon += 1;
    }
  }

  // ── Financial-health tier ─────────────────────────────────────────
  const financialHealthTier: NonNullable<Supplier['financialHealthTier']> =
    supplier.financialHealthTier ?? 'unknown';

  // ── Component → tier ranks ────────────────────────────────────────
  const geoRank = TIER_RANK[geo.level];
  let certRank: number;
  if (expired > 0) certRank = TIER_RANK.high;
  else if (expiringSoon > 0) certRank = TIER_RANK.medium;
  else certRank = TIER_RANK.low;
  let finRank: number;
  switch (financialHealthTier) {
    case 'investment-grade':
      finRank = TIER_RANK.low;
      break;
    case 'speculative':
      finRank = TIER_RANK.medium;
      break;
    case 'distressed':
      finRank = TIER_RANK.critical;
      break;
    case 'unknown':
    default:
      finRank = TIER_RANK.medium;
      break;
  }

  const overallRank = Math.max(geoRank, certRank, finRank);
  const overallTier = RANK_TIER[overallRank];

  return {
    geopoliticalRiskLevel: geo.level,
    geopoliticalMatched: geo.matched,
    certsExpiringSoonCount: expiringSoon,
    certsExpiredCount: expired,
    financialHealthTier,
    overallTier,
  };
}
